import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Modal, Button } from '../common';
import { EditorComposer, Editor, Divider } from '../../editor';
import ToolbarPlugin from '../../editor/plugins/ToolbarPlugin/ToolbarPlugin';
import {
  AlignDropdown,
  BackgroundColorPicker,
  BlockFormatDropdown,
  BoldButton,
  CodeFormatButton,
  FontFamilyDropdown,
  FontSizeDropdown,
  InsertDropdown,
  InsertLinkButton,
  ItalicButton,
  TextColorPicker,
  TextFormatDropdown,
  UnderlineButton,
} from '../../editor/plugins/ToolbarPlugin/components';
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createEquationNode } from '../../editor/nodes/EquationNode';
import { $createImageNode } from '../../editor/nodes/ImageNode';
import { 
  processMathEquation, 
  splitTextAndMath, 
  processTableEquation,
  removeMathDelimiters,
  isDisplayMath 
} from '../utils/latexParser';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// EditorComponent 구현
function EditorComponent({ onLoad, onSave }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // ContentEditable__root 요소를 찾아서 스타일 적용
    setTimeout(() => {
      const editorElement = document.querySelector('div.ContentEditable__root');
      if (editorElement) {
        // 기본 CSS 오버라이드 - 컨텐츠에 맞게 높이 자동 조절
        editorElement.style.padding = '20px';
        editorElement.style.minHeight = '200px';
        editorElement.style.height = 'auto';
        editorElement.style.overflow = 'visible';
        editorElement.style.resize = 'none';
        editorElement.style.maxWidth = 'none';
        editorElement.style.width = '100%';
        editorElement.style.whiteSpace = 'pre-wrap';
        editorElement.style.fontSize = '16px';  // 폰트 크기 줄임
        editorElement.style.fontWeight = '500';  // 문제 미리보기와 동일한 폰트 굵기
      }
      
      // editor-container 스타일 설정
      const editorInnerContainer = document.querySelector('.editor-container');
      if (editorInnerContainer) {
        editorInnerContainer.style.width = '100%';
        editorInnerContainer.style.maxWidth = '465px';
        editorInnerContainer.style.margin = '0 auto';
        editorInnerContainer.style.overflow = 'visible';
        editorInnerContainer.style.height = 'auto';
        editorInnerContainer.style.minHeight = 'auto';
      }
      
      // editor-shell 스타일 오버라이드
      const editorShell = document.querySelector('.editor-shell');
      if (editorShell) {
        editorShell.style.maxWidth = 'none';
        editorShell.style.width = '100%';
        editorShell.style.margin = '0';
        editorShell.style.height = 'auto';
      }
    }, 100);
  }, []);

  const saveEditorState = useCallback(() => {
    const editorState = editor.getEditorState();
    const json = editorState.toJSON();
    onSave(json);
  }, [editor, onSave]);

  const handleMathData = useCallback(async (data) => {
    try {
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        
        let currentParagraph = $createParagraphNode();
        root.append(currentParagraph);

        if (data.mathpix_data?.lined_data) {
          data.mathpix_data.lined_data.forEach(item => {
            if ((item.type !== 'text' && item.type !== 'math' && item.type !== 'table') || 
                item.type === 'chart' || 
                item.type === 'diagram') {
              if (item.content && item.content[0] && 
                  (item.content[0].startsWith('data:image') || item.is_base64)) {
                const imageNode = $createImageNode({
                  altText: 'Solution Image',
                  src: item.content[0],
                  width: item.cnt ? Math.abs(item.cnt[2][0] - item.cnt[0][0]) : undefined,
                  height: item.cnt ? Math.abs(item.cnt[2][1] - item.cnt[0][1]) : undefined,
                  maxWidth: 500,
                  showCaption: false
                });
                currentParagraph.append(imageNode);
                
                currentParagraph = $createParagraphNode();
                root.append(currentParagraph);
              }
            } else if (item.type === 'text') {
              item.content.forEach(text => {
                const mathSegments = splitTextAndMath(text);
                mathSegments.forEach(mathSegment => {
                  if (mathSegment.type === 'text') {
                    const segments = mathSegment.content.split(/(\(\d+\))/);
                    segments.forEach(segment => {
                      const numberMatch = segment.match(/\((\d+)\)/);
                      if (numberMatch) {
                        const num = parseInt(numberMatch[1]);
                        if (num >= 1 && num <= 5) {
                          const circleNumber = ['①', '②', '③', '④', '⑤'][num - 1];
                          const equationNode = $createEquationNode(circleNumber, true);
                          currentParagraph.append(equationNode);
                        } else {
                          const textNode = $createTextNode(segment);
                          currentParagraph.append(textNode);
                        }
                      } else {
                        const textNode = $createTextNode(segment);
                        currentParagraph.append(textNode);
                      }
                    });
                  } else {
                    const equation = processMathEquation(mathSegment.content);
                    const equationNode = $createEquationNode(equation, true);
                    currentParagraph.append(equationNode);
                  }
                });
              });
              
              currentParagraph = $createParagraphNode();
              root.append(currentParagraph);
            } else if (item.type === 'math' || item.type === 'table') {
              const mathContent = item.content[0];
              let equation = mathContent.replace(/^\$\$(.*)\$\$/s, '$1');
              
              if (item.type === 'table') {
                equation = equation
                  .replace(/\\begin\{tabular\}/g, '\\begin{array}')
                  .replace(/\\end\{tabular\}/g, '\\end{array}')
                  .replace(/\$/g, ' ')
                  .replace(/\\hline(?!\s)/g, '\\hline ');
              }
              
              equation = processMathEquation(equation);
              const isDisplayMath = mathContent.startsWith('$$');
              const equationNode = $createEquationNode(equation, !isDisplayMath);
              currentParagraph.append(equationNode);
              
              currentParagraph = $createParagraphNode();
              root.append(currentParagraph);
            }
          });
        }
      });
    } catch (error) {
      console.error('Math data handling error:', error);
    }
  }, [editor]);

  useEffect(() => {
    if (onLoad && typeof onLoad === 'function') {
      onLoad(handleMathData);
    }
  }, [onLoad, handleMathData]);

  return (
    <div className="flex flex-col">
      <div className="editor-wrapper">
        <Editor>
          <ToolbarPlugin>
            {/* <FontFamilyDropdown /> */}
            {/* <FontSizeDropdown /> */}
            {/* <Divider /> */}
            {/* <BoldButton /> */}
            {/* <ItalicButton /> */}
            {/* <UnderlineButton /> */}
            {/* <CodeFormatButton /> */}
            {/* <InsertLinkButton /> */}
            {/* <TextColorPicker /> */}
            {/* <BackgroundColorPicker /> */}
            <TextFormatDropdown />
            <Divider />
            <InsertDropdown />
            <Divider />
            <AlignDropdown />
          </ToolbarPlugin>
        </Editor>
      </div>
      <div className="border-t py-2 px-2 flex justify-end items-center">
        <Button
          onClick={saveEditorState}
          variant="primary"
          className="bg-purple-500 hover:bg-purple-600"
          disabled={!editor}
        >
          풀이 저장
        </Button>
      </div>
    </div>
  );
}

function SolutionRegisterModal({ isOpen, onClose, problemId, onUpdate }) {
  const { getAccessToken, handleApiError } = useAuth();
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showOcrButton, setShowOcrButton] = useState(false);
  const [solutionId, setSolutionId] = useState(null);
  const [status, setStatus] = useState(0);
  const mathDataHandlerRef = useRef(null);
  const canvasRef = useRef(null);
  const [imageScale, setImageScale] = useState(1);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // fetchWithTokenRefresh 함수
  const fetchWithTokenRefresh = useCallback(async (url, options = {}) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('인증 토큰이 없습니다.');
      }
      
      const headers = {
        ...options.headers,
        'Authorization': token,
      };
      
      if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }
      
      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers,
        credentials: 'include',
      });

      if (response.status === 401) {
        handleApiError(new Error('Invalid or expired token'));
        throw new Error('인증이 필요합니다.');
      }

      const data = await response.json();
      
      if (data.status_code === 401) {
        handleApiError(new Error('Invalid or expired token'));
        throw new Error('인증이 필요합니다.');
      }

      return data;
    } catch (error) {
      console.error('API 호출 에러:', error);
      throw error;
    }
  }, [getAccessToken, handleApiError]);

  // solution ID 가져오기
  useEffect(() => {
    const fetchSolutionId = async () => {
      if (!problemId || !isOpen) return;

      try {
        const response = await fetchWithTokenRefresh(`/api/solutions/list/${problemId}`);
        
        if (response?.data?.length > 0) {
          const solution = response.data.reduce((prev, current) => 
            prev.solution_id < current.solution_id ? prev : current
          );
          setSolutionId(solution.solution_id);
          setStatus(solution.solution_info.status);
        }
      } catch (error) {
        console.error('솔루션 정보 조회 실패:', error);
        setError('솔루션 정보 조회에 실패했습니다.');
      }
    };

    if (problemId && isOpen) {
      fetchSolutionId();
    }
  }, [problemId, isOpen, fetchWithTokenRefresh]);

  // 이미지 선택 핸들러
  const handleImageSelect = useCallback((file) => {
    setSelectedImage(file);
    setImageUrl(URL.createObjectURL(file));
    setShowOcrButton(false);
  }, []);

  // 클립보드 붙여넣기 핸들러 (에디터 영역 제외)
  const handlePaste = useCallback((event) => {
    // 에디터 영역에서 붙여넣기하면 무시 (에디터가 자체 처리하도록)
    const target = event.target;
    const isEditorArea = target.closest('.editor-wrapper') || 
                        target.closest('.ContentEditable__root') || 
                        target.closest('[contenteditable="true"]');
    
    if (isEditorArea) {
      return; // 에디터 영역에서는 모달의 붙여넣기 처리하지 않음
    }
    
    const items = event.clipboardData.items;
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      if (item.type.indexOf('image') !== -1) {
        event.preventDefault();
        const blob = item.getAsFile();
        
        if (blob) {
          // 파일명 생성 (현재 시간 기반)
          const now = new Date();
          const fileName = `pasted-image-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}.png`;
          
          // File 객체 생성 (File input과 동일한 형태로)
          const file = new File([blob], fileName, { type: blob.type });
          
          handleImageSelect(file);
        }
        break;
      }
    }
  }, [handleImageSelect]);

  // 드래그 앤 드롭 핸들러
  const handleDrop = useCallback((event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        handleImageSelect(file);
      } else {
        alert('이미지 파일만 업로드할 수 있습니다.');
      }
    }
  }, [handleImageSelect]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
  }, []);

  // 이미지 제거
  const handleRemoveImage = useCallback(() => {
    setSelectedImage(null);
    setImageUrl(null);
    setShowOcrButton(false);
  }, []);

  // 이미지 업로드 핸들러
  const handleImageUpload = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      handleImageSelect(file);
    }
  }, [handleImageSelect]);

  // 모달 닫힐 때 상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setSelectedImage(null);
      setImageUrl(null);
      setIsLoading(false);
      setError(null);
      setShowOcrButton(false);
      setSolutionId(null);
      setStatus(0);
    }
  }, [isOpen]);

  // 모달이 열려있을 때 전역 paste 이벤트 등록
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('paste', handlePaste);
      return () => {
        document.removeEventListener('paste', handlePaste);
      };
    }
  }, [isOpen, handlePaste]);

  // 이미지 업로드
  const handleUpload = useCallback(async () => {
    if (!selectedImage || !solutionId) return;

    setIsLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('image', selectedImage);

      const uploadResponse = await fetchWithTokenRefresh(`/api/solutions/${solutionId}/image`, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse || uploadResponse.status_code !== 201) {
        throw new Error('이미지 업로드 실패');
      }

      setStatus(200);
      setShowOcrButton(true);
    } catch (error) {
      console.error('이미지 업로드 중 오류:', error);
      setError('이미지 업로드에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [selectedImage, solutionId, fetchWithTokenRefresh]);

  // OCR 처리
  const handleOcr = useCallback(async () => {
    if (!solutionId || !mathDataHandlerRef.current) return;

    setIsLoading(true);
    try {
      const response = await fetchWithTokenRefresh(`/api/solutions/${solutionId}/mathpix`, {
        method: 'POST',
        body: JSON.stringify({ mathpix_data_option: true })
      });

      if (!response || response.status_code !== 201) {
        throw new Error('OCR 처리 실패');
      }

      // OCR 데이터 처리
      await mathDataHandlerRef.current(response.data);
      setStatus(300);
      alert('OCR 처리가 완료되었습니다.');
    } catch (error) {
      console.error('OCR 처리 중 오류:', error);
      setError('OCR 처리에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [solutionId, fetchWithTokenRefresh]);

  // setMathDataHandler 함수
  const setMathDataHandler = useCallback((handler) => {
    mathDataHandlerRef.current = handler;
  }, []);

  // 에디터 저장
  const handleEditorSave = useCallback(async (lexicalJson) => {
    if (!solutionId) return;

    try {
      setIsLoading(true);
      const response = await fetchWithTokenRefresh(`/api/solutions/${solutionId}/lexical`, {
        method: 'POST',
        body: JSON.stringify({
          lexical_json: lexicalJson
        })
      });

      if (!response || (response.status_code !== 200 && response.status_code !== 201)) {
        throw new Error('에디터 데이터 저장 실패');
      }

      alert('풀이가 성공적으로 저장되었습니다.');
      onUpdate(); // 부모 컴포넌트 데이터 갱신
      onClose(); // 모달 닫기
    } catch (error) {
      console.error('에디터 데이터 저장 중 오류:', error);
      setError('풀이 저장에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, [solutionId, fetchWithTokenRefresh, onUpdate, onClose]);

  const initialState = () => {
    const paragraph = $createParagraphNode();
    const text = $createTextNode('풀이를 작성해주세요.');
    paragraph.append(text);
    const root = $getRoot();
    root.append(paragraph);
    root.selectEnd();
  };

  // 닫기 핸들러
  const handleCloseModal = useCallback(() => {
    setShowCloseConfirm(true);
  }, []);

  const confirmClose = useCallback(() => {
    setShowCloseConfirm(false);
    onClose();
  }, [onClose]);

  const cancelClose = useCallback(() => {
    setShowCloseConfirm(false);
  }, []);

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={
        <div className="flex items-baseline gap-2">
          <span>풀이 등록</span>
          {solutionId && <span className="text-sm text-gray-500">- SID: {solutionId}</span>}
        </div>
      }
      size="full"
      className="flex flex-col"
      bodyClassName="flex flex-col flex-1 overflow-auto p-4"
      zIndex={9999800}
    >
      <div className="flex flex-1 flex-col overflow-y-auto p-4">
        {/* 에러 메시지 */}
        {error && (
          <div className="mb-4 rounded-3xl border border-red-200 bg-red-50 p-3 text-sm text-red-600">
            {error}
          </div>
        )}

        {/* 로딩 상태 */}
        {isLoading && (
          <div className="mb-4 text-sm text-blue-500">처리 중...</div>
        )}
        
        {/* 이미지 업로드 영역 */}
        <div className="mb-4">
          {status >= 100 && (
            <div className="mb-4">
              {/* 파일 선택 버튼 */}
              <div className="mb-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="mb-2 block w-full text-sm text-gray-500
                            file:mr-4 file:rounded-full file:border-0
                            file:bg-blue-50 file:px-4
                            file:py-2 file:text-sm
                            file:font-semibold file:text-blue-700
                            hover:file:bg-blue-100"
                />
              </div>
              
              {/* 드래그 앤 드롭 영역 */}
              <div 
                className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
              >
                <div className="space-y-2">
                  <div className="text-gray-500">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="text-sm text-gray-600">
                    여기에 이미지를 <span className="font-medium text-blue-600">드래그 앤 드롭</span> 하세요
                  </div>
                  <div className="text-xs text-gray-500">
                    또는 <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-lg">Ctrl+V</kbd>로 클립보드에서 붙여넣기
                    <br />
                    (에디터 외부에서만 동작)
                  </div>
                  <div className="text-xs text-gray-400">
                    PNG, JPG, GIF 파일만 업로드 가능
                  </div>
                </div>
              </div>
            </div>
          )}

          {imageUrl && status >= 100 && (
            <div className="space-y-2">
              {/* 이미지 컨테이너 */}
              <div className="relative overflow-auto rounded-lg border border-gray-200 bg-gray-50">
                <div 
                  className="relative inline-block min-w-full p-4"
                  style={{ transform: `scale(${imageScale})`, transformOrigin: 'top left' }}
                >
                  <img 
                    src={imageUrl} 
                    alt="Selected" 
                    className="size-auto max-w-full"
                  />
                  <canvas
                    ref={canvasRef}
                    className="pointer-events-none absolute left-0 top-0"
                  />
                  <Button
                    onClick={handleRemoveImage}
                    variant="secondary"
                    size="sm"
                    className="absolute right-2 top-2 bg-red-500 text-white hover:bg-red-600"
                  >
                    ×
                  </Button>
                </div>
              </div>
              
              {/* 확대/축소 컨트롤 */}
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => setImageScale(Math.max(0.5, imageScale - 0.1))}
                  className="rounded-full border border-gray-300 p-1 hover:bg-gray-100"
                  title="축소"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                  </svg>
                </button>
                <span className="min-w-[60px] text-center text-sm font-medium">
                  {Math.round(imageScale * 100)}%
                </span>
                <button
                  onClick={() => setImageScale(Math.min(2, imageScale + 0.1))}
                  className="rounded-full border border-gray-300 p-1 hover:bg-gray-100"
                  title="확대"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
                <button
                  onClick={() => setImageScale(1)}
                  className="ml-2 rounded-full border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100"
                  title="원본 크기"
                >
                  원본
                </button>
              </div>

              <div className="flex space-x-4 mt-4">
                {!showOcrButton ? (
                  <Button
                    onClick={handleUpload}
                    variant="primary"
                    disabled={isLoading || !solutionId}
                    loading={isLoading}
                  >
                    이미지 업로드
                  </Button>
                ) : (
                  <Button
                    onClick={handleOcr}
                    variant="primary"
                    className="bg-green-500 hover:bg-green-600"
                    disabled={isLoading}
                    loading={isLoading}
                  >
                    OCR 처리 시작
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* 에디터 영역 */}
        <div className="mb-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-800">수식 편집기</h3>
            <div className="text-sm text-gray-500">
              풀이 내용을 편집하세요
            </div>
          </div>
          <div className="rounded-lg border border-gray-300 bg-white shadow-sm">
            <div className="w-full [&_.editor-container]:!font-inherit [&_div.editor-equation]:block [&_span.editor-equation]:inline-block">
              <EditorComposer initialEditorState={initialState}>
                <EditorComponent 
                  onLoad={setMathDataHandler} 
                  onSave={handleEditorSave}
                />
              </EditorComposer>
            </div>
          </div>
        </div>
      </div>
    </Modal>
    
    {/* 닫기 확인 팝업 */}
    {showCloseConfirm && (
      <Modal
        isOpen={showCloseConfirm}
        onClose={cancelClose}
        title="확인"
        size="small"
        zIndex={9999900}
      >
        <div className="p-4">
          <p className="mb-4">
            수정한 내용이 저장되지 않았습니다.<br />
            정말로 닫으시겠습니까?
          </p>
          <div className="flex justify-end gap-2">
            <Button
              onClick={cancelClose}
              variant="secondary"
              className="px-4 py-2"
            >
              취소
            </Button>
            <Button
              onClick={confirmClose}
              variant="primary"
              className="bg-red-500 hover:bg-red-600 px-4 py-2"
            >
              닫기
            </Button>
          </div>
        </div>
      </Modal>
    )}
    </>
  );
}

export default SolutionRegisterModal; 